import { logger } from '../lib/logger';

interface Props {
  format: string;
  codec: string;
  stream: MediaStream;
  recordingId: string;
  /** Receives the raw full-recording blob once at stop; awaited for the tail. */
  onBackup: (blob: Blob) => Promise<void>;
}

/** Emits one raw backup Blob when stopped. */
export class BackupCaptureRecorder {
  doneRecording = false;
  private mediaRecorder: MediaRecorder;
  private stopPromise: Promise<void> = Promise.resolve();
  private resolveStop: (() => void) | null = null;

  constructor(private props: Props) {
    const mimeType = `audio/${props.format};codecs=${props.codec}`;
    this.mediaRecorder = new MediaRecorder(this.props.stream, { mimeType: mimeType });

    this.mediaRecorder.ondataavailable = async (event) => {
      try {
        logger.info({
          event_type: 'BACKUP_RECORDED',
          recording_id: this.props.recordingId,
          backup_size_bytes: event.data.size,
        });
        await this.props.onBackup(event.data);
      } finally {
        this.doneRecording = true;
        this.resolveFinalStop();
      }
    };

    this.mediaRecorder.start();
  }

  /** Persist-bound: resolves once the backup handler has settled. */
  stop(): Promise<void> {
    if (this.mediaRecorder.state === 'inactive') return this.stopPromise;
    this.stopPromise = new Promise<void>((resolve) => {
      this.resolveStop = resolve;
    });
    this.mediaRecorder.stop();
    return this.stopPromise;
  }

  /** Stops capture without publishing the final blob (logout). */
  discard() {
    this.mediaRecorder.ondataavailable = null;
    this.mediaRecorder.onerror = null;
    if (this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    this.doneRecording = true;
    this.resolveFinalStop();
  }

  private resolveFinalStop(): void {
    this.resolveStop?.();
    this.resolveStop = null;
  }
}
